"use client";

import type * as React from "react";
import { Input } from "@/components/ui/input";
import { Field, FieldDescription, FieldError, FieldLabel, useFieldWrapperContext } from "./field-wrapper";
import type { FieldBindingProps } from "./types";

export interface InputFieldProps extends FieldBindingProps<string> {
  /** Omit only where the surrounding row already says what the text is; pass `ariaLabel` instead. */
  label?: string;
  ariaLabel?: string;
  description?: string;
  placeholder?: string;
  type?: React.HTMLInputTypeAttribute;
  /**
   * Numeric keypad on touch devices without `type="number"`.
   *
   * A number input reports an empty string for intermediate invalid text
   * ("-", "1e", "3."), so a debounced autosave can persist a blank while the
   * user is still typing. `inputMode` gets the keypad and leaves the value
   * exactly as entered.
   */
  inputMode?: "decimal" | "numeric";
  min?: number;
  max?: number;
  step?: number;
  maxLength?: number;
  autoComplete?: React.InputHTMLAttributes<HTMLInputElement>["autoComplete"];
  autoFocus?: boolean;
  disabled?: boolean;
  isRequired?: boolean;
  variant?: "default" | "inline";
}

export function InputField({
  value,
  onChange,
  onBlur,
  errors,
  isTouched,
  isRequired,
  label,
  ariaLabel,
  description,
  placeholder,
  type = "text",
  inputMode,
  min,
  max,
  step,
  maxLength,
  autoComplete,
  autoFocus,
  disabled,
  variant = "default",
}: InputFieldProps) {
  if (variant === "inline") {
    return (
      <Input
        type={type}
        inputMode={inputMode}
        min={min}
        max={max}
        step={step}
        maxLength={maxLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={isRequired}
        autoFocus={autoFocus}
        disabled={disabled}
        className="!h-auto !border-none !shadow-none !ring-0 !outline-none !bg-transparent !rounded-none !px-0 !text-xl font-medium placeholder:text-muted-foreground/50"
      />
    );
  }

  return (
    <Field errors={errors} isTouched={isTouched} isRequired={isRequired}>
      {label && <FieldLabel>{label}</FieldLabel>}
      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldInput
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        type={type}
        inputMode={inputMode}
        min={min}
        max={max}
        step={step}
        maxLength={maxLength}
        placeholder={placeholder}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        disabled={disabled}
        isRequired={isRequired}
        hasLabel={!!label}
        ariaLabel={label ? undefined : ariaLabel}
      />
      <FieldError />
    </Field>
  );
}

function FieldInput({
  value,
  onChange,
  onBlur,
  type,
  inputMode,
  min,
  max,
  step,
  maxLength,
  placeholder,
  autoComplete,
  autoFocus,
  disabled,
  hasLabel,
  isRequired,
  ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  type: React.HTMLInputTypeAttribute;
  inputMode?: "decimal" | "numeric";
  min?: number;
  max?: number;
  step?: number;
  maxLength?: number;
  placeholder?: string;
  autoComplete?: React.InputHTMLAttributes<HTMLInputElement>["autoComplete"];
  autoFocus?: boolean;
  disabled?: boolean;
  hasLabel: boolean;
  isRequired?: boolean;
  ariaLabel?: string;
}) {
  const { id, hasError } = useFieldWrapperContext();
  return (
    <Input
      id={id}
      aria-label={ariaLabel}
      type={type}
      inputMode={inputMode}
      min={min}
      max={max}
      step={step}
      maxLength={maxLength}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      autoComplete={autoComplete}
      required={isRequired}
      autoFocus={autoFocus}
      disabled={disabled}
      aria-invalid={hasError}
      aria-describedby={hasError ? `${id}-error` : undefined}
      className={hasLabel ? "mt-1" : ""}
    />
  );
}
